import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Theme,
  useMediaQuery,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  AutoAwesome as AutoAwesomeIcon,
  ChevronRight as ChevronRightIcon,
  FirstPage as FirstPageIcon,
  LastPage as LastPageIcon,
  Phone as PhoneIcon,
  CalendarMonth as CalendarMonthIcon,
  Groups as GroupsIcon,
  Notes as NotesIcon,
  Close as CloseIcon,
  Person as PersonIcon,
} from '@mui/icons-material';

import { FormHeader } from '../FormHeader';
import { BackButton } from 'components';

import RefreshIcon from '@mui/icons-material/Refresh';

import { Spinner } from 'components';
import { useSnackbar } from 'notistack';
import { useApiRequests } from 'hooks/useApiRequests';
import { useDialog } from 'hooks';
import { useTranslation } from 'react-i18next';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { isHipaaLink } from 'config/productProfile';
import { AINoteItemStatusEnum, ApiCallHistory } from 'types';
import { getIntlLocale, parseIdentity } from 'utils';
import { TwoOptionsDialog } from 'components';

import styles from './CallHistory.module.scss';

/* =====================
   Types
===================== */

type CallHistoryItem = {
  callHistoryId: string;
  room: string;
  roomSid?: string | null;
  hostClinicUserId: string;
  hostName: string;
  participantsCount?: number;
  participants: string[];
  startDate: string;
  endDate: string | null;
  durationMinutes: number;
  isActive: boolean;
};

type Props = {
  initialRowsPerPage?: number;
  onBackToSettingsClick?: () => void;
};

type DatePreset = 'today' | 'yesterday' | 'last7' | 'last14' | 'last30' | 'last12months' | 'custom';

type DateButtonProps = {
  label: Date;
  locale: string;
  min?: Date;
  max?: Date;
  onChange: (date: Date) => void;
};

type CallSummaryAINote = {
  id: string;
  status: AINoteItemStatusEnum;
  errorMessage: string;
};

const normalizeKey = (value: string) => value.replace(/[^a-z0-9]/gi, '').toLowerCase();

const toStringOrNull = (value: unknown): string | null => {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
};

const toStatusEnum = (value: unknown): AINoteItemStatusEnum | null => {
  if (typeof value === 'number' && value >= 0 && value <= AINoteItemStatusEnum.RecordingStoppedNotByUserError) {
    return value as AINoteItemStatusEnum;
  }

  if (typeof value !== 'string') return null;

  const normalized = normalizeKey(value);
  if (normalized === 'notstarted') return AINoteItemStatusEnum.NotStarted;
  if (normalized === 'processing') return AINoteItemStatusEnum.Processing;
  if (normalized === 'error') return AINoteItemStatusEnum.Error;
  if (normalized === 'compleated' || normalized === 'completed' || normalized === 'complete') {
    return AINoteItemStatusEnum.Compleated;
  }
  if (normalized === 'tooshortrecording' || normalized === 'tooshort') {
    return AINoteItemStatusEnum.TooShortRecordingError;
  }
  if (normalized === 'tooshortrecordingerror') return AINoteItemStatusEnum.TooShortRecordingError;
  if (normalized === 'recordingstoppednotbyuser' || normalized === 'stoppednotbyuser') {
    return AINoteItemStatusEnum.RecordingStoppedNotByUserError;
  }
  if (normalized === 'recordingstoppednotbyusererror') return AINoteItemStatusEnum.RecordingStoppedNotByUserError;
  return null;
};

const extractAINoteFromSummary = (summary: unknown): CallSummaryAINote | null => {
  if (!summary || typeof summary !== 'object') return null;
  const source = summary as Record<string, unknown>;

  const nested =
    (source.aiNote as Record<string, unknown> | undefined) ||
    (source.aINote as Record<string, unknown> | undefined) ||
    (source.aiNotes as Record<string, unknown> | undefined) ||
    (source.aINotes as Record<string, unknown> | undefined) ||
    (source.aiProgressNote as Record<string, unknown> | undefined) ||
    (source.aIProgressNote as Record<string, unknown> | undefined) ||
    (source.aiProgressNotes as Record<string, unknown> | undefined) ||
    (source.aIProgressNotes as Record<string, unknown> | undefined);

  const container = nested ?? source;
  const id =
    toStringOrNull(container.id) ||
    toStringOrNull(container.aiNoteId) ||
    toStringOrNull(container.aINoteId) ||
    toStringOrNull(container.aiNotesId) ||
    toStringOrNull(container.aINotesId) ||
    toStringOrNull(container.noteId);

  const status =
    toStatusEnum(container.status) ||
    toStatusEnum(container.aiNoteStatus) ||
    toStatusEnum(container.aINoteStatus) ||
    toStatusEnum(container.aiNotesStatus) ||
    toStatusEnum(container.aINotesStatus) ||
    toStatusEnum(container.noteStatus);

  const errorMessage =
    toStringOrNull(container.errorMessage) ||
    toStringOrNull(container.aiNoteErrorMessage) ||
    toStringOrNull(container.aINoteErrorMessage) ||
    toStringOrNull(container.aiNotesErrorMessage) ||
    toStringOrNull(container.aINotesErrorMessage) ||
    '';

  if (status === null) return null;

  return {
    id: id ?? '',
    status,
    errorMessage,
  };
};

const toTimestamp = (value: unknown): number | null => {
  if (typeof value !== 'string' && !(value instanceof Date)) return null;
  const timestamp = new Date(value as string | Date).getTime();
  return Number.isFinite(timestamp) ? timestamp : null;
};

const extractAINoteFromListItem = (item: unknown): CallSummaryAINote | null => {
  if (!item || typeof item !== 'object') return null;
  const source = item as Record<string, unknown>;
  const id = toStringOrNull(source.id) || toStringOrNull(source.aiNoteId) || toStringOrNull(source.aINoteId);
  const status = toStatusEnum(source.status) || toStatusEnum(source.aiNoteStatus) || toStatusEnum(source.noteStatus);
  if (!id || status === null) return null;
  return {
    id,
    status,
    errorMessage: toStringOrNull(source.errorMessage) || toStringOrNull(source.message) || '',
  };
};

const getAINoteRoomToken = (item: Record<string, unknown>): string => {
  return normalizeRoomToken(item.room) || normalizeRoomToken(item.roomName) || normalizeRoomToken(item.roomSid);
};

const getAINoteRoomSid = (item: Record<string, unknown>): string => {
  return normalizeRoomSid(item.roomSid) || normalizeRoomSid(item.RoomSid) || normalizeRoomSid(item.roomSID);
};

const getAINoteStartMs = (item: Record<string, unknown>): number | null => {
  return (
    toTimestamp(item.startedAt) ??
    toTimestamp(item.startDate) ??
    toTimestamp(item.startAt) ??
    toTimestamp(item.createdAt) ??
    null
  );
};

const getAINoteEndMs = (item: Record<string, unknown>, startMs: number | null): number | null => {
  return toTimestamp(item.endedAt) ?? toTimestamp(item.endDate) ?? toTimestamp(item.endAt) ?? startMs;
};

const normalizeRoomToken = (value: unknown): string => {
  if (typeof value !== 'string') return '';
  return value.trim().toLowerCase().replace(/^\//, '');
};

const normalizeRoomSid = (value: unknown): string => {
  if (typeof value !== 'string') return '';
  return value.trim().toUpperCase();
};

const getRoomAliases = (room: string): string[] => {
  const normalized = normalizeRoomToken(room);
  if (!normalized) return [];

  const aliases = new Set<string>([normalized]);
  const parts = normalized.split('__').filter(Boolean);
  if (parts.length > 0) {
    aliases.add(parts[0]);
    aliases.add(parts[parts.length - 1]);
  }

  return Array.from(aliases);
};

const findAINoteFromListByRoomAndTime = (
  roomSid: string | null | undefined,
  room: string,
  summaryStart: unknown,
  summaryEnd: unknown,
  summaryParticipantsCount: number | null | undefined,
  items: unknown[],
): CallSummaryAINote | null => {
  const startMs = toTimestamp(summaryStart);
  const endMs = toTimestamp(summaryEnd) ?? startMs;
  if (startMs == null || endMs == null) return null;
  const normalizedSummaryRoomSid = normalizeRoomSid(roomSid);

  const sidScopedItems =
    normalizedSummaryRoomSid.length > 0
      ? items.filter((x) => {
          if (!x || typeof x !== 'object') return false;
          return getAINoteRoomSid(x as Record<string, unknown>) === normalizedSummaryRoomSid;
        })
      : items;

  // If summary has RoomSid but no AI note has that RoomSid, treat as no-note.
  if (normalizedSummaryRoomSid.length > 0 && sidScopedItems.length === 0) {
    return null;
  }

  const roomAliases = getRoomAliases(room);
  const roomNotes = sidScopedItems.filter((x) => {
    if (!x || typeof x !== 'object') return false;
    const candidate = x as Record<string, unknown>;
    const candidateRoom = getAINoteRoomToken(candidate);
    if (!candidateRoom) return false;

    if (roomAliases.includes(candidateRoom)) return true;

    return roomAliases.some((alias) => candidateRoom.includes(alias) || alias.includes(candidateRoom));
  });

  const noteCandidates = roomNotes.length > 0 ? roomNotes : sidScopedItems;
  if (noteCandidates.length === 0) return null;

  const overlapPaddingMs = 30 * 60 * 1000;
  let best: {
    note: CallSummaryAINote;
    score: number;
    distance: number;
  } | null = null;

  for (const raw of noteCandidates) {
    const candidate = raw as Record<string, unknown>;
    const note = extractAINoteFromListItem(candidate);
    if (!note) continue;

    const noteStart = getAINoteStartMs(candidate);
    const noteEnd = getAINoteEndMs(candidate, noteStart);
    if (noteStart == null || noteEnd == null) continue;

    const overlap = noteStart <= endMs + overlapPaddingMs && noteEnd >= startMs - overlapPaddingMs;
    const distance = Math.abs(noteStart - startMs);
    const candidateParticipants = typeof candidate.participantsCount === 'number' ? candidate.participantsCount : null;
    const participantsMatch =
      summaryParticipantsCount == null ||
      candidateParticipants == null ||
      summaryParticipantsCount === candidateParticipants;

    const candidateRoom = getAINoteRoomToken(candidate);
    const roomMatch =
      candidateRoom.length > 0 &&
      (roomAliases.includes(candidateRoom) ||
        roomAliases.some((alias) => candidateRoom.includes(alias) || alias.includes(candidateRoom)));

    // Ranking:
    // 1) room + overlap + participants
    // 2) room + overlap
    // 3) overlap + participants
    // 4) overlap
    // 5) room-only
    let score = 0;
    if (roomMatch && overlap && participantsMatch) score = 5;
    else if (roomMatch && overlap) score = 4;
    else if (overlap && participantsMatch) score = 3;
    else if (overlap) score = 2;
    else if (roomMatch) score = 1;
    else continue;

    if (!best || score > best.score || (score === best.score && distance < best.distance)) {
      best = { note, score, distance };
    }
  }

  return best?.note ?? null;
};

function DateButton({ label, locale, min, max, onChange }: DateButtonProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div className={styles.compactDateWrapper}>
      {/* Visible fake input */}
      <div className={styles.compactDateDisplay} onClick={() => inputRef.current?.showPicker()}>
        {label.toLocaleDateString(locale)}
        <CalendarTodayIcon className={styles.calendarIcon} />
      </div>

      {/* Real date input (hidden, controlled) */}
      <input
        ref={inputRef}
        type='date'
        value={label.toISOString().slice(0, 10)}
        min={min?.toISOString().slice(0, 10)}
        max={max?.toISOString().slice(0, 10)}
        onChange={(e) => {
          if (!e.target.value) return;

          const [y, m, d] = e.target.value.split('-').map(Number);
          const next = new Date(y, m - 1, d);
          next.setHours(0, 0, 0, 0);

          if (min && next < min) return;
          if (max && next > max) return;

          onChange(next);
        }}
        onWheel={(e) => {
          // 🚫 Prevent month desync caused by scroll wheel
          e.preventDefault();
          e.stopPropagation();
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
        tabIndex={-1}
        className={styles.hiddenDateInput}
      />
    </div>
  );
}
const rowsPerPageOptions = [5, 10, 25];

/* =====================
   Component
===================== */

export function CallHistory({ initialRowsPerPage, onBackToSettingsClick }: Props) {
  const { t, i18n } = useTranslation('settings');
  const tableRef = useRef<HTMLDivElement>(null);
  const { enqueueSnackbar } = useSnackbar();
  const { showDialog } = useDialog();
  const {
    getCallHistoryPaged,
    getCallHistoryExport,
    getCallHistoryHosts,
    getCallHistoryMinDate,
    getRoomHistory,
    getAINotes,
    downloadNote,
    processTooShortRecording,
    processStoppedNotByUserRecording,
  } = useApiRequests();

  const [loading, setLoading] = useState(false);
  const [refreshToken, setRefreshToken] = useState(0);
  const [items, setItems] = useState<CallHistoryItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [resolvedParticipantsTotals, setResolvedParticipantsTotals] = useState<Record<string, number>>({});

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage ?? 10);

  // 🔹 Default date range = last week
  const [startUtc, setStartUtc] = useState<Date>(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d;
  });

  const [endUtc, setEndUtc] = useState<Date>(() => new Date());
  const hostRangeKey = `${startUtc.toISOString().slice(0, 10)}_${endUtc.toISOString().slice(0, 10)}`;
  const [minCallDateUtc, setMinCallDateUtc] = useState<Date | null>(null);

  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const [hostClinicUserId, setHostClinicUserId] = useState<string | null>(null);
  const [datePreset, setDatePreset] = useState<DatePreset>('last7');
  const [allHosts, setAllHosts] = useState<{ id: string; name: string }[]>([]);
  const [isSummaryOpen, setIsSummaryOpen] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryActionLoading, setSummaryActionLoading] = useState(false);
  const [selectedSummary, setSelectedSummary] = useState<(ApiCallHistory & { hostName?: string }) | null>(null);
  const [selectedSummaryAINote, setSelectedSummaryAINote] = useState<CallSummaryAINote | null>(null);
  const locale = getIntlLocale(i18n.resolvedLanguage || i18n.language || 'en');

  const getTimeZoneAbbreviation = (date: Date) => {
    const parts = new Intl.DateTimeFormat(locale, { timeZoneName: 'short' }).formatToParts(date);
    return parts.find((part) => part.type === 'timeZoneName')?.value ?? 'Local';
  };

  /* =====================
     Data Load
  ===================== */

  useEffect(() => {
    let isCancelled = false;

    async function load() {
      try {
        setLoading(true);

        const offset = (currentPage - 1) * rowsPerPage;

        const result = await getCallHistoryPaged(
          startUtc.toISOString(),
          endUtc.toISOString(),
          hostClinicUserId,
          offset,
          rowsPerPage,
        );

        if (!isCancelled) {
          setItems(result.items);
          setTotalCount(result.totalCount);
        }
      } catch {
        if (!isCancelled) {
          enqueueSnackbar(t('callHistory.failedToLoadCallHistory'), { variant: 'error' });
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    }

    load();

    return () => {
      isCancelled = true;
    };
  }, [currentPage, rowsPerPage, startUtc, endUtc, hostClinicUserId, refreshToken, enqueueSnackbar, t]);

  useEffect(() => {
    let cancelled = false;

    async function resolveParticipantsTotals() {
      if (!items.length) return;

      const unresolved = items.filter((x) => resolvedParticipantsTotals[x.callHistoryId] == null);
      if (!unresolved.length) return;

      for (const item of unresolved) {
        if (cancelled) return;

        try {
          const details = await getRoomHistory(item.room);
          const guestsFromParticipants = details.participants?.length ?? 0;
          const guestsFromDetails = details.participantDetails?.length ?? 0;
          const total = Math.max(guestsFromParticipants, guestsFromDetails) + 1;

          if (!cancelled) {
            setResolvedParticipantsTotals((prev) => ({
              ...prev,
              [item.callHistoryId]: total,
            }));
          }
        } catch {
          // non-blocking fallback: leave unresolved, row will show fallback count
        }
      }
    }

    resolveParticipantsTotals();

    return () => {
      cancelled = true;
    };
  }, [items, resolvedParticipantsTotals, getRoomHistory]);

  useEffect(() => {
    let cancelled = false;

    async function loadHosts() {
      try {
        const hosts = await getCallHistoryHosts(startUtc.toISOString(), endUtc.toISOString());

        if (!cancelled) {
          setAllHosts(
            hosts.map((h) => ({
              id: h.hostClinicUserId,
              name: h.hostName,
            })),
          );
        }
      } catch {
        // non-blocking
      }
    }

    loadHosts();

    return () => {
      cancelled = true;
    };
  }, [hostRangeKey]);

  const applyDatePreset = (preset: DatePreset) => {
    const now = new Date();
    const start = new Date(now);

    switch (preset) {
      case 'today':
        start.setHours(0, 0, 0, 0);
        now.setHours(23, 59, 59, 999);
        break;
      case 'yesterday':
        start.setDate(now.getDate() - 1);
        start.setHours(0, 0, 0, 0);
        now.setDate(now.getDate() - 1);
        now.setHours(23, 59, 59, 999);
        break;
      case 'last7':
        start.setDate(now.getDate() - 7);
        break;
      case 'last14':
        start.setDate(now.getDate() - 14);
        break;
      case 'last30':
        start.setDate(now.getDate() - 30);
        break;
      case 'last12months':
        start.setFullYear(now.getFullYear() - 1);
        break;
      case 'custom':
        return;
    }

    start.setHours(0, 0, 0, 0);
    now.setHours(23, 59, 59, 999);

    if (minCallDateUtc && start < minCallDateUtc) {
      start.setTime(minCallDateUtc.getTime());
    }

    setStartUtc(start);
    setEndUtc(now);
  };

  function clampDates(start: Date, end: Date): { start: Date; end: Date } {
    if (end < start) {
      return { start, end: new Date(start) };
    }

    return { start, end };
  }

  useEffect(() => {
    let cancelled = false;

    async function loadMinDate() {
      try {
        const minDateIso = await getCallHistoryMinDate();

        if (!cancelled && minDateIso) {
          const d = new Date(minDateIso);
          d.setHours(0, 0, 0, 0);
          setMinCallDateUtc(d);
        }
      } catch {
        // non-blocking — picker will just fall back
      }
    }

    loadMinDate();

    return () => {
      cancelled = true;
    };
  }, []);

  /* =====================
     Pagination
  ===================== */

  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, totalCount);

  const paginationText = `${startIndex + 1}-${endIndex} ${t('callHistory.of')} ${totalCount}`;

  const handleRowsPerPageChange = (event: SelectChangeEvent<string>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(1);
  };

  const handleFirstPageClick = () => setCurrentPage(1);
  const handlePreviousPageClick = () => setCurrentPage((prev) => Math.max(prev - 1, 1));
  const handleNextPageClick = () => setCurrentPage((prev) => (endIndex < totalCount ? prev + 1 : prev));
  const handleLastPageClick = () => setCurrentPage(Math.ceil(totalCount / rowsPerPage));

  /* =====================
     Formatting
  ===================== */

  const formatDuration = (minutes: number) => {
    if (minutes < 60) return `${minutes} ${t('callHistory.minAbbrev')}`;

    const hours = Math.floor(minutes / 60);
    const remaining = minutes % 60;

    return remaining > 0
      ? `${hours} ${t('callHistory.hourAbbrev')} ${remaining} ${t('callHistory.minuteAbbrev')}`
      : `${hours} ${t('callHistory.hourAbbrev')}`;
  };

  const formatTableDate = (value: string | null) => {
    if (!value) return '--';
    return new Date(value).toLocaleDateString(locale, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatTableTime = (value: string | null) => {
    if (!value) return t('callHistory.active');
    return new Date(value).toLocaleTimeString(locale, {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const formatClockDuration = (secondsValue?: number | null) => {
    if (secondsValue == null) return '--:--';
    const safe = Math.max(0, secondsValue);
    const minutes = Math.floor(safe / 60);
    const seconds = safe % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  const formatSummaryDurationShort = (start: Date | string, end: Date | string | null) => {
    if (!end) return t('callHistory.active');
    const totalSeconds = Math.max(0, Math.floor((new Date(end).getTime() - new Date(start).getTime()) / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    return `${minutes}${t('callHistory.minutesShort')} ${seconds}${t('callHistory.secondsShort')}`;
  };

  const getWaitColor = (waitTimeSeconds?: number | null) => ((waitTimeSeconds ?? 0) < 300 ? '#1DAA5A' : '#E53935');

  const formatLocalTime = (value: Date | string | null) => {
    if (!value) return t('callHistory.active');
    const date = new Date(value);
    const formattedTime = date.toLocaleTimeString(locale, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
    return `${formattedTime} ${getTimeZoneAbbreviation(date)}`;
  };

  const formatLocalDate = (value: Date | string) =>
    new Date(value).toLocaleDateString(locale, {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    });

  /* =====================
     Export
  ===================== */

  const handleExport = async () => {
    try {
      const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
      const blob = await getCallHistoryExport(startUtc.toISOString(), endUtc.toISOString(), null, clientTimeZone);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = url;
      link.download = 'call-history.csv';

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.error ?? t('callHistory.exportFailed'), { variant: 'error' });
    }
  };

  const handleOpenSummary = async (item: CallHistoryItem) => {
    try {
      setSummaryLoading(true);
      setSelectedSummaryAINote(null);
      const details = await getRoomHistory(item.room);
      setSelectedSummary({ ...details, hostName: details.hostName || item.hostName });

      let aiNote = extractAINoteFromSummary(details);

      if (!aiNote) {
        try {
          const pageSize = 200;
          const noteResult = await getAINotes(1, pageSize);
          let allNoteItems = [...(noteResult.items ?? [])];
          const totalPages = Math.max(1, noteResult.pagesCount ?? 1);

          for (let page = 2; page <= totalPages; page += 1) {
            const next = await getAINotes(page, pageSize);
            const nextItems = next.items ?? [];
            if (!nextItems.length) break;
            allNoteItems = [...allNoteItems, ...nextItems];
          }

          const summaryRoomSid =
            toStringOrNull((details as unknown as Record<string, unknown>).roomSid) ||
            toStringOrNull((details as unknown as Record<string, unknown>).RoomSid) ||
            toStringOrNull((item as unknown as Record<string, unknown>).roomSid) ||
            null;
          aiNote = findAINoteFromListByRoomAndTime(
            summaryRoomSid,
            item.room,
            details.startDate,
            details.endDate,
            item.participantsCount,
            allNoteItems,
          );
          if (!aiNote) {
            // no-op: absence of matching AI note is an expected state
          }
        } catch {
          // non-blocking fallback
        }
      }

      setSelectedSummaryAINote(aiNote);
      setIsSummaryOpen(true);
    } catch {
      enqueueSnackbar(t('callHistory.failedToLoadCallSummary'), { variant: 'error' });
    } finally {
      setSummaryLoading(false);
    }
  };

  const getDisplayParticipants = (summary: ApiCallHistory & { hostName?: string }) => {
    const detailed = summary.participantDetails ?? [];
    const hostName = (summary.hostName ?? '').trim().toLowerCase();

    if (detailed.length > 0) {
      return detailed
        .filter((p) => p.name?.trim().toLowerCase() !== hostName)
        .map((p) => ({
          name: p.name,
          waitTimeSeconds: p.waitTimeSeconds ?? null,
          participationTimeSeconds: p.participationTimeSeconds ?? null,
        }));
    }

    return (summary.participants ?? [])
      .map((identity) => parseIdentity(identity ?? '')[0]?.trim() ?? '')
      .filter((name) => Boolean(name) && name.toLowerCase() !== hostName)
      .map((name) => ({
        name,
        waitTimeSeconds: null as number | null,
        participationTimeSeconds: null as number | null,
      }));
  };

  const handleAINoteErrorAction = () => {
    if (!selectedSummaryAINote) return;

    if (selectedSummaryAINote.status === AINoteItemStatusEnum.Error) {
      enqueueSnackbar(selectedSummaryAINote.errorMessage || t('callHistory.aiProgressNoteGenerationFailed'), {
        variant: 'error',
      });
      return;
    }

    if (
      selectedSummaryAINote.status === AINoteItemStatusEnum.TooShortRecordingError ||
      selectedSummaryAINote.status === AINoteItemStatusEnum.RecordingStoppedNotByUserError
    ) {
      const isTooShort = selectedSummaryAINote.status === AINoteItemStatusEnum.TooShortRecordingError;
      const dialog = showDialog(TwoOptionsDialog, {
        title: isTooShort ? t('callHistory.audioRecordingTooShortTitle') : t('callHistory.somethingWentWrongTitle'),
        text: isTooShort ? t('callHistory.audioRecordingTooShortText') : t('callHistory.somethingWentWrongText'),
        confirmText: isTooShort ? t('callHistory.yes') : t('callHistory.yesTryAgain'),
        sx: { '.MuiPaper-root': { maxWidth: '300px !important' } },
        onConfirm: async () => {
          try {
            setSummaryActionLoading(true);
            if (isTooShort) {
              await processTooShortRecording(selectedSummaryAINote.id);
            } else {
              await processStoppedNotByUserRecording(selectedSummaryAINote.id);
            }
            setSelectedSummaryAINote((prev) =>
              prev
                ? {
                    ...prev,
                    status: AINoteItemStatusEnum.Processing,
                  }
                : prev,
            );
          } catch (error: any) {
            enqueueSnackbar(error?.message || t('callHistory.failedToReprocessAINote'), { variant: 'error' });
          } finally {
            setSummaryActionLoading(false);
            dialog.destroy();
          }
        },
      });
    }
  };

  const handleDownloadAINote = (isPdf: boolean) => async () => {
    if (!selectedSummaryAINote?.id) return;

    try {
      setSummaryActionLoading(true);
      const response: any = await downloadNote(selectedSummaryAINote.id, isPdf);
      const blob = new Blob([response], {
        type: isPdf ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });
      const fileName = `Progress_Note.${isPdf ? 'pdf' : 'docx'}`;

      const downloadUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(downloadUrl);
    } catch (error: any) {
      enqueueSnackbar(
        t('callHistory.downloadReportError', { message: error?.message || t('callHistory.unknownError') }),
        {
          variant: 'error',
        },
      );
    } finally {
      setSummaryActionLoading(false);
    }
  };

  /* =====================
     Render
  ===================== */

  return (
    <>
      {isMobile && onBackToSettingsClick && (
        <BackButton text={t('callHistory.backToSettings')} onClick={onBackToSettingsClick} />
      )}

      <div className={styles.container}>
        {/* TAB HEADER */}
        <FormHeader items={[{ text: t('callHistory.callHistoryUpper'), isSelected: true }]} />

        {loading ? (
          <Spinner small />
        ) : (
          /* WHITE CARD BODY */
          <div className={styles.main}>
            {/* Header */}
            <div className={styles.header}>
              <div className={styles.filters}>
                {/* Host filter */}
                <Select
                  size='small'
                  displayEmpty
                  value={hostClinicUserId ?? ''}
                  onChange={(e) => {
                    setHostClinicUserId(e.target.value || null);
                    setCurrentPage(1);
                  }}
                  sx={{ minWidth: 200 }}
                >
                  <MenuItem value=''>
                    <em>{t('callHistory.allHosts')}</em>
                  </MenuItem>

                  {allHosts.map((host) => (
                    <MenuItem key={host.id} value={host.id}>
                      {host.name}
                    </MenuItem>
                  ))}
                </Select>

                {/* DATE FILTER */}
                {datePreset !== 'custom' ? (
                  <Select
                    size='small'
                    value={datePreset}
                    onChange={(e) => {
                      const preset = e.target.value as DatePreset;
                      setDatePreset(preset);
                      setCurrentPage(1);
                      applyDatePreset(preset);
                    }}
                    sx={{ minWidth: 180 }}
                  >
                    <MenuItem value='today'>{t('callHistory.today')}</MenuItem>
                    <MenuItem value='yesterday'>{t('callHistory.yesterday')}</MenuItem>
                    <MenuItem value='last7'>{t('callHistory.last7Days')}</MenuItem>
                    <MenuItem value='last14'>{t('callHistory.last14Days')}</MenuItem>
                    <MenuItem value='last30'>{t('callHistory.last30Days')}</MenuItem>
                    <MenuItem value='last12months'>{t('callHistory.last12Months')}</MenuItem>
                    <MenuItem value='custom'>{t('callHistory.customEllipsis')}</MenuItem>
                  </Select>
                ) : (
                  <Stack direction='row' spacing={1} alignItems='center'>
                    {/* COMPACT DATE RANGE */}
                    <Stack direction='row' spacing={1} alignItems='center'>
                      <DateButton
                        label={startUtc}
                        locale={locale}
                        min={minCallDateUtc ?? undefined}
                        max={new Date()}
                        onChange={(date) => {
                          date.setHours(0, 0, 0, 0);

                          const { start, end } = clampDates(date, endUtc);
                          setStartUtc(start);
                          setEndUtc(end);
                          setCurrentPage(1);
                        }}
                      />

                      <Typography variant='body2' className={styles.dateTo}>
                        {t('callHistory.to')}
                      </Typography>

                      <div className={styles.customDateRow}>
                        <DateButton
                          label={endUtc}
                          locale={locale}
                          min={startUtc < (minCallDateUtc ?? startUtc) ? minCallDateUtc ?? startUtc : startUtc}
                          max={new Date()}
                          onChange={(date) => {
                            date.setHours(23, 59, 59, 999);
                            setEndUtc(date);
                            setCurrentPage(1);
                          }}
                        />

                        <Typography
                          variant='body5'
                          className={styles.clearFilter}
                          onClick={() => {
                            setDatePreset('last7');
                            applyDatePreset('last7');
                            setCurrentPage(1);
                          }}
                        >
                          {t('callHistory.clearFilter')}
                        </Typography>
                      </div>
                    </Stack>
                  </Stack>
                )}
              </div>

              <div className={styles.actions}>
                <Tooltip title={t('callHistory.refresh')}>
                  <span>
                    <IconButton
                      onClick={() => {
                        setCurrentPage(1); // reset pagination
                        setRefreshToken((v) => v + 1); // force reload
                      }}
                      disabled={loading}
                      size='small'
                    >
                      <RefreshIcon className={`${styles.refreshIcon} ${loading ? styles.refreshing : ''}`} />
                    </IconButton>
                  </span>
                </Tooltip>

                <Button variant='outlined' onClick={handleExport} disabled={loading}>
                  {t('callHistory.exportCsv')}
                </Button>
              </div>
            </div>

            {/* TABLE */}
            <div className={styles.table} ref={tableRef}>
              <Grid className={styles.tableHeader}>
                <Typography variant='subtitle2'>{t('callHistory.host')}</Typography>
                <Typography variant='subtitle2'>{t('callHistory.totalParticipants')}</Typography>
                <Typography variant='subtitle2'>{t('callHistory.date')}</Typography>
                <Typography variant='subtitle2'>{t('callHistory.start')}</Typography>
                <Typography variant='subtitle2'>{t('callHistory.end')}</Typography>
                <Typography variant='subtitle2' className={styles.center}>
                  {t('callHistory.duration')}
                </Typography>
              </Grid>

              <Box>
                {items.map((item) => {
                  const participantsFromList = item.participants.length + 1;
                  const participantsFromCount = item.participantsCount ?? 0;
                  const fallbackTotalCount = Math.max(participantsFromList, participantsFromCount);
                  const totalCount = resolvedParticipantsTotals[item.callHistoryId] ?? fallbackTotalCount;
                  const participantNames = !isHipaaLink ? item.participants.filter(Boolean).join(', ') : '';

                  return (
                    <Grid
                      key={item.callHistoryId}
                      className={styles.tableItem}
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleOpenSummary(item)}
                    >
                      <Typography variant='body1' noWrap>
                        {item.hostName}
                      </Typography>

                      <Typography variant='body1' noWrap>
                        <strong>({totalCount})</strong>
                        {!isHipaaLink && participantNames ? ` ${participantNames}` : ''}
                      </Typography>

                      <Typography variant='body1'>{formatTableDate(item.startDate)}</Typography>

                      <Typography variant='body1'>{formatTableTime(item.startDate)}</Typography>

                      <Typography variant='body1'>
                        {item.isActive ? t('callHistory.active') : formatTableTime(item.endDate)}
                      </Typography>

                      <Typography variant='body1' className={styles.center}>
                        {formatDuration(item.durationMinutes)}
                      </Typography>
                    </Grid>
                  );
                })}
              </Box>
            </div>

            {/* PAGINATION */}
            <Stack className={styles.paginationRow}>
              <div className={styles.topRow}>
                <Stack direction='row' spacing={1} alignItems='center'>
                  <Typography>{t('callHistory.rowsPerPage')}</Typography>
                  <Select
                    value={rowsPerPage.toString()}
                    onChange={handleRowsPerPageChange}
                    IconComponent={ChevronRightIcon}
                  >
                    {rowsPerPageOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </Stack>

                <Typography>{paginationText}</Typography>
              </div>

              <Stack direction='row' spacing={1}>
                <IconButton onClick={handleFirstPageClick}>
                  <FirstPageIcon />
                </IconButton>
                <IconButton onClick={handlePreviousPageClick}>
                  <ArrowBackIcon />
                </IconButton>
                <IconButton onClick={handleNextPageClick}>
                  <ArrowForwardIcon />
                </IconButton>
                <IconButton onClick={handleLastPageClick}>
                  <LastPageIcon />
                </IconButton>
              </Stack>
            </Stack>
          </div>
        )}
      </div>

      <Dialog
        open={isSummaryOpen}
        onClose={() => {
          if (summaryActionLoading) return;
          setIsSummaryOpen(false);
        }}
        className={styles.summaryDialog}
      >
        <DialogTitle className={styles.summaryHeader}>
          <div className={styles.summaryHeaderLeft}>
            <span className={styles.summaryHeaderIcon}>
              <PhoneIcon fontSize='small' />
            </span>
            <Typography component='p' variant='h2'>
              {t('callHistory.callSummary')}
            </Typography>
          </div>
          <IconButton
            size='small'
            className={styles.summaryCloseIcon}
            onClick={() => {
              if (summaryActionLoading) return;
              setIsSummaryOpen(false);
            }}
          >
            <CloseIcon fontSize='small' />
          </IconButton>
        </DialogTitle>
        <DialogContent className={styles.summaryDialogContent}>
          {summaryLoading || !selectedSummary ? (
            <Spinner small />
          ) : (
            <div className={styles.summaryBody}>
              <div className={styles.summaryPanel}>
                <Typography component='p' className={styles.summarySectionTitle}>
                  <CalendarMonthIcon fontSize='small' />
                  {t('callHistory.sessionDetails')}
                </Typography>
                <div className={styles.summaryDetailsLayout}>
                  <div className={styles.summaryDetailsGrid}>
                    <Typography className={styles.summaryLabel}>{t('callHistory.hostLabel')}</Typography>
                    <Typography className={styles.summaryValue}>{selectedSummary.hostName || '-'}</Typography>
                    <Typography className={styles.summaryLabel}>{t('callHistory.dateLabel')}</Typography>
                    <Typography className={styles.summaryValue}>
                      {formatLocalDate(selectedSummary.startDate)}
                    </Typography>
                    <Typography className={styles.summaryLabel}>{t('callHistory.startTimeLabel')}</Typography>
                    <Typography className={styles.summaryValue}>
                      {formatLocalTime(selectedSummary.startDate)}
                    </Typography>
                    <Typography className={styles.summaryLabel}>{t('callHistory.endTimeLabel')}</Typography>
                    <Typography className={styles.summaryValue}>{formatLocalTime(selectedSummary.endDate)}</Typography>
                  </div>
                  <div className={styles.summaryDurationCard}>
                    <Typography className={styles.summaryDurationLabel}>
                      {t('callHistory.totalDurationLabel')}
                    </Typography>
                    <Typography className={styles.summaryDurationValue}>
                      {formatSummaryDurationShort(selectedSummary.startDate, selectedSummary.endDate)}
                    </Typography>
                  </div>
                </div>
              </div>

              <div className={styles.summaryPanel}>
                <Typography component='p' className={styles.summarySectionTitle}>
                  <GroupsIcon fontSize='small' />
                  {t('callHistory.participants')}
                </Typography>
                <div className={styles.summaryParticipantsScroller}>
                  <div className={styles.summaryParticipantsTable}>
                    <div className={styles.summaryParticipantsHeader}>
                      <span>{t('callHistory.participant')}</span>
                      <span>{t('callHistory.waitTime')}</span>
                      <span>{t('callHistory.duration')}</span>
                      <span>{t('callHistory.role')}</span>
                    </div>
                    <div className={styles.summaryParticipantRow}>
                      <span className={styles.summaryParticipantName}>
                        <PersonIcon fontSize='small' />
                        {selectedSummary.hostName || t('callHistory.host')}
                      </span>
                      <span className={styles.summaryWaitBadge}>—</span>
                      <span>
                        {formatClockDuration(
                          Math.max(
                            0,
                            Math.floor(
                              ((selectedSummary.endDate ? new Date(selectedSummary.endDate) : new Date()).getTime() -
                                new Date(selectedSummary.startDate).getTime()) /
                                1000,
                            ),
                          ),
                        )}
                      </span>
                      <span>{t('callHistory.host')}</span>
                    </div>
                    {getDisplayParticipants(selectedSummary).map((p, index) => (
                      <div key={`${p.name}-${index}`} className={styles.summaryParticipantRow}>
                        <span className={styles.summaryParticipantName}>
                          <PersonIcon fontSize='small' />
                          {p.name}
                        </span>
                        <span
                          className={styles.summaryWaitBadge}
                          style={{
                            color: getWaitColor(p.waitTimeSeconds),
                            borderColor: getWaitColor(p.waitTimeSeconds),
                            backgroundColor: `${getWaitColor(p.waitTimeSeconds)}14`,
                          }}
                        >
                          {p.waitTimeSeconds != null ? formatClockDuration(p.waitTimeSeconds) : '--'}
                        </span>
                        <span>{formatClockDuration(p.participationTimeSeconds)}</span>
                        <span>{t('callHistory.guest')}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className={styles.summaryPanel}>
                <div className={styles.summaryBottomGrid}>
                  <div className={styles.summaryBottomColumn}>
                    <Typography component='p' className={styles.summarySectionTitle}>
                      <NotesIcon fontSize='small' />
                      {t('callHistory.sessionNotes')}
                    </Typography>
                    <Typography className={styles.summaryNotesText}>
                      {selectedSummary.meetingNotes || t('callHistory.noNotesEntered')}
                    </Typography>
                  </div>

                  {selectedSummaryAINote && <div className={styles.summaryBottomDivider} />}

                  {selectedSummaryAINote && (
                    <div className={styles.summaryBottomColumn}>
                      <Typography component='p' className={styles.summarySectionTitle}>
                        <AutoAwesomeIcon fontSize='small' />
                        {t('callHistory.aiProgressNotes')}
                      </Typography>

                      {selectedSummaryAINote.status === AINoteItemStatusEnum.NotStarted && (
                        <Typography className={styles.summaryNotesText}>{t('callHistory.notStarted')}</Typography>
                      )}

                      {selectedSummaryAINote.status === AINoteItemStatusEnum.Processing && (
                        <Box className={styles.summaryStatusChip}>
                          <Typography className={styles.summaryStatusChipText}>
                            {t('callHistory.processing')}
                          </Typography>
                        </Box>
                      )}

                      {(selectedSummaryAINote.status === AINoteItemStatusEnum.Error ||
                        selectedSummaryAINote.status === AINoteItemStatusEnum.TooShortRecordingError ||
                        selectedSummaryAINote.status === AINoteItemStatusEnum.RecordingStoppedNotByUserError) && (
                        <Button
                          variant='contained'
                          className={styles.summaryAINotesErrorButton}
                          disabled={summaryActionLoading || !selectedSummaryAINote.id}
                          onClick={handleAINoteErrorAction}
                        >
                          {t('callHistory.viewError')}
                        </Button>
                      )}

                      {selectedSummaryAINote.status === AINoteItemStatusEnum.Compleated && (
                        <Stack direction='row' gap={1} className={styles.summaryAINotesActions}>
                          <Button
                            variant='outlined'
                            className={styles.summaryAINotesButton}
                            disabled={summaryActionLoading || !selectedSummaryAINote.id}
                            onClick={handleDownloadAINote(true)}
                          >
                            PDF
                          </Button>

                          <Button
                            variant='contained'
                            className={styles.summaryAINotesButton}
                            disabled={summaryActionLoading || !selectedSummaryAINote.id}
                            onClick={handleDownloadAINote(false)}
                          >
                            DOCX
                          </Button>
                        </Stack>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default CallHistory;
